import type { RouteLocationRaw } from 'vue-router'
import { SIGNUP_MATCH_ROUTE_NAME } from '@/pages/signup/config/signup.constants'

export type AppSection =
  | 'messages'
  | 'calendar'
  | 'journal'
  | 'payments'
  | 'vault'
  | 'infoLibrary'
  | 'records'
  | 'calling'
  | 'accountSettings'

export interface AccessContext {
  isRestricted: boolean
  hasActivity?: boolean
}

export function hasUserActivity(
  activityCounts?: Record<string, number | null | undefined>
): boolean {
  return Object.values(activityCounts ?? {}).some((count) => Number(count) > 0)
}

export const PRIMARY_NAV_SECTIONS: AppSection[] = [
  'messages',
  'calling',
  'calendar',
  'payments',
  'infoLibrary',
  'records',
  'journal',
  'vault'
]

export const APP_DEFAULT_ROUTE_NAMES = {
  login: 'login',
  matchedHome: 'messages',
  unmatched: SIGNUP_MATCH_ROUTE_NAME,
  restrictedPaywall: 'postMatch',
  restrictedPaywallLegacy: 'accountSettings/accountTypeStep1'
} as const

const restrictedAllowedSections: AppSection[] = ['accountSettings']
export function isSectionAllowedForUser(
  section: AppSection,
  context: AccessContext
): boolean {
  const baseAllowed = !context.isRestricted
    ? true
    : restrictedAllowedSections.includes(section)

  if (section === 'records' && context.hasActivity !== undefined) {
    return baseAllowed || context.hasActivity
  }

  return baseAllowed
}

export interface PostLoginContext extends AccessContext {
  hasMatch: boolean
  isRestricted: boolean
  isRestrictedLegacyUser?: boolean
}

export function getLoginRoute(returnTo?: string): RouteLocationRaw {
  return {
    name: APP_DEFAULT_ROUTE_NAMES.login,
    query: returnTo ? { returnTo } : undefined
  }
}

export function getMatchedHomeRoute(): RouteLocationRaw {
  return { name: APP_DEFAULT_ROUTE_NAMES.matchedHome }
}

export function getUnmatchedRoute(): RouteLocationRaw {
  return { name: APP_DEFAULT_ROUTE_NAMES.unmatched }
}

export function getRestrictedPaywallRoute(): RouteLocationRaw {
  return { name: APP_DEFAULT_ROUTE_NAMES.restrictedPaywall }
}

export function getDefaultRouteForUser(
  context: PostLoginContext,
  returnTo?: RouteLocationRaw | null
): RouteLocationRaw {
  if (!context.hasMatch) {
    return getUnmatchedRoute()
  }

  if (!context.isRestricted) {
    if (returnTo) {
      return returnTo
    }
    return getMatchedHomeRoute()
  }

  return returnTo == '/records' ? returnTo : getRestrictedPaywallRoute()
}
